<template>
  <v-card :disabled="isDisabled">
    <UserBadge :have-permissions="!isDisabled" :read-only="!canWrite"/>
    <v-col cols="12" v-if="!canWrite" no-gutters class="blue-grey--text text--lighten-1 pt-0 pb-0">
      <v-icon color="red">mdi-alert-box-outline</v-icon> {{ $t('perms.nochange') }}
    </v-col>
    <v-card-title>
      <div class="mr-2">{{ $t('settings.screenPop.title') }}</div>
    </v-card-title>
    <v-card-text class="pr-8">
      {{ $t('settings.screenPop.help') }}
      <v-simple-table dense class="mb-4" style="max-width: 500px">
        <template v-slot:default>
          <tbody>
            <tr>
              <td class="text-right">{{ $t('settings.screenPop.variable.callerid_number') }}:</td>
              <td><div v-pre>{{callerid_number}}</div></td>
            </tr>
            <tr>
              <td class="text-right">{{ $t('settings.screenPop.variable.callerid_name') }}:</td>
              <td><div v-pre>{{callerid_name}}</div></td>
            </tr>
            <tr>
              <td class="text-right">{{ $t('settings.screenPop.variable.extension') }}:</td>
              <td><div v-pre>{{extension}}</div></td>
            </tr>
            <tr>
              <td class="text-right">{{ $t('settings.screenPop.variable.extension_name') }}:</td>
              <td><div v-pre>{{extension_name}}</div></td>
            </tr>
            <tr>
              <td class="text-right">{{ $t('settings.screenPop.variable.did') }}:</td>
              <td><div v-pre>{{did}}</div></td>
            </tr>
            <tr>
              <td class="text-right">{{ $t('settings.screenPop.variable.wait_sec') }}:</td>
              <td><div v-pre>{{wait_sec}}</div></td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      <v-select
        :disabled="!canWrite"
        prepend-icon="mdi-toggle-switch"
        v-model="type"
        :items="typeList"
        item-text="label"
        item-value="id"
        :label="$t('settings.screenPop.type')"
      ></v-select>
      <v-select
        v-if="showTrigger"
        :disabled="!canWrite"
        prepend-icon="mdi-phone-alert"
        v-model="trigger"
        :items="triggerList"
        item-text="label"
        item-value="id"
        :label="$t('settings.screenPop.trigger')"
      ></v-select>
      <v-textarea
        v-if="showUrl"
        :disabled="!canWrite"
        prepend-icon="mdi-link"
        v-model="url"
        :label="$t('settings.screenPop.url')"
        :rules="[ v => v.length > 0 || 'URL is required']"
        row-height="1"
        auto-grow
      ></v-textarea>
    </v-card-text>
    <v-divider></v-divider>
    <v-card-actions>
      <v-icon color="red" class="mr-3" v-if="isDisabled" small>mdi-lock</v-icon>
      <v-btn text @click="save" :disabled="!canWrite || !isValid" class="save-btn">
        {{ $t('settings.screenPop.save') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import UserBadge from '../user_badge'

export default {
  name: 'settings-screen-pop',
  data () {
    return {
      defaultType: 'none',
      defaultTrigger: 'ringing',
      defaultUrl: ''
    }
  },
  components: {
    UserBadge
  },
  computed: {
    ...mapGetters({
      activeSubAccount: 'settings/activeSubAccount',
      isOwnerCurUser: 'settings/isOwnerCurUser',
      screenPopWrite: 'permissions/screenPopWrite',
      screenPopWriteFor: 'permissions/screenPopWriteFor',
      screenPopData: 'settings/screenPop'
    }),
    isDisabled: function () {
      if (!this.activeSubAccount || this.isOwnerCurUser) {
        return false
      }
      return !this.activeSubAccount.features?.includes('screen_pop')
    },
    canWrite: function () {
      if (!this.activeSubAccount || this.isOwnerCurUser) {
        return this.screenPopWrite
      }
      return this.screenPopWriteFor(this.activeSubAccount?.subscriber_id)
    },
    isValid: function () {
      switch (this.type) {
        case 'url':
          return this.url.length > 0
        default:
          return true
      }
    },
    showTrigger: function () {
      return this.type === 'url'
    },
    showUrl: function () {
      return this.type === 'url'
    },
    typeList: function () {
      return [
        { id: 'none', label: this.$t('settings.screenPop.typeList.none') },
        { id: 'url', label: this.$t('settings.screenPop.typeList.url') }
      ]
    },
    triggerList: function () {
      return [
        { id: 'ringing', label: this.$t('settings.screenPop.triggerList.ringing') },
        { id: 'answered', label: this.$t('settings.screenPop.triggerList.answered') }
      ]
    },
    type: {
      get: function () {
        return this.getTypeValue(this.screenPopData.type)
      },
      set: function (val) {
        this.setScreenPopType(val)
      }
    },
    trigger: {
      get: function () {
        return this.getTriggerValue(this.screenPopData.trigger)
      },
      set: function (val) {
        this.setScreenPopTrigger(val)
      }
    },
    url: {
      get: function () {
        return this.getUrlValue(this.screenPopData.url)
      },
      set: function (val) {
        this.setScreenPopUrl(val)
      }
    }
  },
  methods: {
    ...mapActions({
      setScreenPop: 'settings/setScreenPop',
      setScreenPopType: 'settings/setScreenPopType',
      setScreenPopTrigger: 'settings/setScreenPopTrigger',
      setScreenPopUrl: 'settings/setScreenPopUrl'
    }),
    getTypeValue: function (type) {
      const val = this.typeList.find(item => item.id === type)
      return val ? val.id : this.defaultType
    },
    getTriggerValue: function (trigger) {
      const val = this.triggerList.find(item => item.id === trigger)
      return val ? val.id : this.defaultTrigger
    },
    getUrlValue: function (val) {
      return val || this.defaultUrl
    },
    save: function () {
      this.setScreenPop({
        type: this.getTypeValue(this.screenPopData.type),
        trigger: this.getTriggerValue(this.screenPopData.trigger),
        url: this.getUrlValue(this.screenPopData.url)
      })
    }
  }
}
</script>

<style scoped>
</style>
